<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <div class="login-page">
    <div class="login-wrap">
      <!-- Logo -->
      <div class="login-logo">
        <span class="login-logo-icon">🌱</span>
        <div>
          <div class="login-logo-zh">綠憩植研</div>
          <div class="login-logo-en">GREEN RICH GEAM</div>
        </div>
      </div>

      <h2 class="login-title">登入帳號</h2>
      <p class="login-sub">歡迎回來，請輸入您的帳號密碼</p>

      <div class="form-group">
        <label>帳號</label>
        <input
          v-model="account"
          type="text"
          placeholder="請輸入帳號"
          @keyup.enter="login"
        />
      </div>

      <div class="form-group">
        <label>密碼</label>
        <input
          v-model="password"
          type="password"
          placeholder="請輸入密碼"
          @keyup.enter="login"
        />
      </div>

      <!-- 記住我 -->
      <div class="remember-wrap">
        <label class="remember-label">
          <input type="checkbox" v-model="rememberMe" />
          <span>記住我（7 天）</span>
        </label>
      </div>

      <button class="btn-login" @click="login" :disabled="loading">
        {{ loading ? "登入中..." : "登入" }}
      </button>

      <p v-if="errorMsg" class="error-msg">{{ errorMsg }}</p>

      <div class="divider">
        <span>或</span>
      </div>

      <router-link to="/" class="btn-skip">不登入，繼續逛網站</router-link>
    </div>
  </div>
</template>

<script setup>
import { ref } from "vue";
import { useRouter } from "vue-router";
import axios from "axios";

const router = useRouter();
const account = ref("");
const password = ref("");
const rememberMe = ref(false);
const loading = ref(false);
const errorMsg = ref("");

const login = async () => {
  errorMsg.value = "";

  if (!account.value || !password.value) {
    errorMsg.value = "請輸入帳號與密碼";
    return;
  }

  loading.value = true;
  try {
    const res = await axios.post("/login/login", {
      account: account.value,
      password: password.value,
    });

    if (res.data.rtnCode === "200") {
      const token = res.data.token;
      const displayName = res.data.displayName;

      // 根據「記住我」決定儲存方式
      if (rememberMe.value) {
        // 記住我 → localStorage（關閉瀏覽器不清除）
        localStorage.setItem("jwtToken", token);
        localStorage.setItem("displayName", displayName);
        localStorage.setItem("lastActivity", Date.now().toString());
      } else {
        // 不記住 → sessionStorage（關閉瀏覽器就清除）
        sessionStorage.setItem("jwtToken", token);
        sessionStorage.setItem("displayName", displayName);
      }

      axios.defaults.headers.common["Authorization"] = "Bearer " + token;

      // 登入成功跳到首頁
      router.push("/");
    } else {
      errorMsg.value = res.data.errMsg || "登入失敗，請確認帳號密碼";
    }
  } catch (err) {
    errorMsg.value =
      "登入失敗：" + (err.response?.data?.message || err.message);
  } finally {
    loading.value = false;
  }
};
</script>

<style scoped>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.login-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #eaf2e0 0%, #f7f5ef 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  font-family: "Noto Sans TC", "Microsoft JhengHei", sans-serif;
}

.login-wrap {
  background: white;
  border-radius: 20px;
  padding: 48px 40px;
  width: 100%;
  max-width: 420px;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.1);
}

/* Logo */
.login-logo {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 32px;
}

.login-logo-icon {
  font-size: 36px;
}

.login-logo-zh {
  font-size: 18px;
  font-weight: 700;
  color: #1c2b1a;
}

.login-logo-en {
  font-size: 11px;
  color: #9baa97;
  letter-spacing: 1px;
}

.login-title {
  font-size: 24px;
  font-weight: 700;
  color: #1c2b1a;
  margin-bottom: 8px;
}

.login-sub {
  font-size: 14px;
  color: #9baa97;
  margin-bottom: 32px;
}

/* Form */
.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #3d4f3a;
  margin-bottom: 8px;
}

.form-group input {
  width: 100%;
  padding: 12px 16px;
  border: 1.5px solid #ddd;
  border-radius: 8px;
  font-size: 15px;
  font-family: inherit;
  color: #1c2b1a;
  transition: border-color 0.3s;
}

.form-group input:focus {
  outline: none;
  border-color: #4e7b4f;
  box-shadow: 0 0 0 3px rgba(78, 123, 79, 0.1);
}

/* 記住我 */
.remember-wrap {
  margin-bottom: 24px;
}

.remember-label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 14px;
  color: #6b7f68;
}

.remember-label input[type="checkbox"] {
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: #4e7b4f;
}

/* 登入按鈕 */
.btn-login {
  width: 100%;
  padding: 14px;
  background: linear-gradient(135deg, #4e7b4f 0%, #2d5016 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  font-family: inherit;
  margin-bottom: 16px;
}

.btn-login:hover:not(:disabled) {
  opacity: 0.9;
  transform: translateY(-1px);
}

.btn-login:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

/* 錯誤訊息 */
.error-msg {
  color: #ff6b6b;
  font-size: 14px;
  text-align: center;
  margin-bottom: 16px;
}

/* 分隔線 */
.divider {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 20px 0;
  color: #9baa97;
  font-size: 13px;
}

.divider::before,
.divider::after {
  content: "";
  flex: 1;
  height: 1px;
  background: #eee;
}

/* 跳過按鈕 */
.btn-skip {
  display: block;
  width: 100%;
  padding: 12px;
  background: #f7f5ef;
  color: #6b7f68;
  border: 1.5px solid #ddd;
  border-radius: 8px;
  text-align: center;
  text-decoration: none;
  font-size: 14px;
  transition: all 0.3s;
}

.btn-skip:hover {
  background: #eaf2e0;
  border-color: #4e7b4f;
  color: #4e7b4f;
}

/* Responsive */
@media (max-width: 480px) {
  .login-wrap {
    padding: 32px 24px;
  }
}
</style>
